import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ReactFlow, Background, Controls, Handle, MarkerType, Position,
  type Edge, type Node, type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Building2, Mail, ShoppingBag, Sparkles, Maximize2, X,
} from "lucide-react";
import type { DerivedLead } from "../../lib/studioFromGraph";
import { useStudioLeadsLive } from "../../lib/useStudioLeadsLive";
import { cls } from "../../lib/cls";
import { formatCompactMoney } from "../../lib/i18n/format";

/* Radial mindmap of the studio's lead pipeline.
 *
 * Center  = the studio
 * Ring 1  = the 5 stages (NEW / QUALIFIED / PROPOSAL / WON / LOST)
 * Ring 2  = individual leads, each carrying their tier + age + next-action
 *
 * Polar-coords layout — zero extra deps beyond xyflow + dagre we already
 * ship. Same Apple cream/ink palette as the rest of the cockpit. */

interface CenterNodeData extends Record<string, unknown> {
  label: string;
  count: number;
}
interface StageNodeData extends Record<string, unknown> {
  stage: DerivedLead["stage"];
  label: string;
  count: number;
}
interface LeadNodeData extends Record<string, unknown> {
  lead: DerivedLead;
}

function CenterNode({ data }: { data: CenterNodeData }): JSX.Element {
  return (
    <div className="group">
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <div className="rounded-2xl bg-ink-950 text-cream shadow-deep px-4 py-3 text-center" style={{ width: 200 }}>
        <div className="text-[10px] uppercase tracking-wider opacity-70">Studio pipeline</div>
        <div className="font-newyork text-[15px] font-semibold mt-0.5 leading-tight">{data.label}</div>
        <div className="text-[11px] opacity-80 mt-1">
          <span className="tabular">{data.count}</span> active leads
        </div>
      </div>
    </div>
  );
}

const STAGE_TONE: Record<DerivedLead["stage"], string> = {
  new:       "bg-cream-dark ring-border text-ink-950",
  qualified: "bg-sky-50 ring-sky-200 text-sky-800",
  proposal:  "bg-amber-50 ring-amber-200 text-amber-800",
  won:       "bg-fern/10 ring-fern/30 text-fern",
  lost:      "bg-foyr/10 ring-foyr/30 text-foyr-deep",
};
const STAGE_LABEL: Record<DerivedLead["stage"], string> = {
  new: "New", qualified: "Qualified", proposal: "Proposal", won: "Won", lost: "Lost",
};

function StageNode({ data }: { data: StageNodeData }): JSX.Element {
  return (
    <div className="group">
      <Handle type="target" position={Position.Left}  className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <div className={cls("rounded-xl ring-1 px-3 py-2 text-center cursor-default", STAGE_TONE[data.stage])} style={{ width: 140 }}>
        <div className="text-[10.5px] uppercase tracking-wider font-mono">{data.label}</div>
        <div className="font-newyork text-[14px] font-semibold mt-0.5 leading-tight tabular">{data.count}</div>
      </div>
    </div>
  );
}

const TIER_TONE = {
  standard: "bg-cream-dark text-ink-700 border-border",
  premium:  "bg-foyr/10 text-foyr-deep border-foyr/30",
  luxury:   "bg-ink-950 text-cream border-ink-950",
} as const;

function LeadNode({ data }: { data: LeadNodeData }): JSX.Element {
  const { lead } = data;
  return (
    <div className="group">
      <Handle type="target" position={Position.Left}  className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <div
        className="rounded-xl bg-cream ring-1 ring-border shadow-[0_1px_2px_rgba(0,0,0,0.04)] px-3 py-2 cursor-default transition-all group-hover:-translate-y-0.5 group-hover:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.10)]"
        style={{ width: 220 }}
      >
        <div className="font-newyork text-[13px] font-semibold text-ink-950 leading-tight truncate">{lead.name}</div>
        <div className="text-[10px] text-ink-500 italic mt-0.5 truncate">{lead.city}</div>
        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
          <span className={cls("pill text-[9px] border", TIER_TONE[lead.budget_tier])}>{lead.budget_tier}</span>
          <span className="pill text-[9px] bg-cream-dark border border-border text-ink-700 tabular">{lead.age_days}d</span>
          {lead.value_inr ? (
            <span className="pill text-[9px] bg-cream-dark border border-border text-ink-700 tabular">
              {formatCompactMoney(lead.value_inr)}
            </span>
          ) : null}
        </div>
        <div className="text-[10.5px] text-foyr-deep mt-1.5 leading-tight truncate">
          <Sparkles className="inline w-2.5 h-2.5 mr-0.5" />
          {lead.next_action.label}
        </div>
      </div>
    </div>
  );
}

const mindmapNodeTypes: NodeTypes = {
  center: CenterNode as unknown as NodeTypes[string],
  stage:  StageNode  as unknown as NodeTypes[string],
  lead:   LeadNode   as unknown as NodeTypes[string],
};

const STAGES: ReadonlyArray<DerivedLead["stage"]> = ["new", "qualified", "proposal", "won", "lost"];

/* Layout: 3 columns laid out L→R so each lead card owns its own
 * non-overlapping vertical slot.
 *
 *   col 0 (x = 40)        col 1 (x = 380)        col 2 (x = 660)
 *   ┌────────────┐
 *   │  STUDIO    │ ──┬──▶ [ NEW (n) ]      ───▶  Singh family
 *   │  PIPELINE  │   │                              Iyer studio
 *   └────────────┘   ├──▶ [ QUALIFIED ]    ───▶  Patel siblings
 *                    │                              Rao residence
 *                    ├──▶ [ PROPOSAL ]     ───▶  Anika & Rohan
 *                    ├──▶ [ WON ]          ───▶  Verma family / Mehta / …
 *                    └──▶ [ LOST ]         ───▶  Ahuja & sons
 *
 * Each stage owns a vertical band sized to fit its leads (one card
 * per row, 90px slot). No two leaf cards can collide because they
 * live in disjoint y-ranges. */
const COL_CENTER_X    = 40;
const COL_STAGE_X     = 380;
const COL_LEAF_X      = 660;
const LEAF_SLOT_H     = 90;   // y-gap per lead card
const STAGE_MIN_BAND  = 110;  // a stage with 0 leads still gets this much height
const BAND_PAD        = 26;   // visual breathing room between stages
const LEAF_W          = 220;
const CENTER_H        = 86;
const STAGE_H         = 60;
const LEAF_H          = 84;

function layoutMindmap(leads: readonly DerivedLead[]): { nodes: Node[]; edges: Edge[]; width: number; height: number } {
  const byStage = new Map<DerivedLead["stage"], DerivedLead[]>();
  for (const s of STAGES) byStage.set(s, []);
  for (const l of leads) {
    const list = byStage.get(l.stage);
    if (list) list.push(l);
  }

  // Compute each stage's band height = max(leads * slot, minimum).
  const bandHeights = STAGES.map((s) => {
    const n = byStage.get(s)?.length ?? 0;
    return Math.max(STAGE_MIN_BAND, n * LEAF_SLOT_H + 24);
  });
  const totalHeight = bandHeights.reduce((a, b) => a + b + BAND_PAD, -BAND_PAD);
  const bandCenters: number[] = [];
  let cursor = 0;
  for (const h of bandHeights) {
    bandCenters.push(cursor + h / 2);
    cursor += h + BAND_PAD;
  }

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Center — vertically centered on the whole stack.
  const centerY = totalHeight / 2 - CENTER_H / 2;
  nodes.push({
    id: "center", type: "center",
    position: { x: COL_CENTER_X, y: centerY },
    data: { label: "Across all projects", count: leads.length } as CenterNodeData,
    draggable: false, selectable: false,
  });

  const stagePalette: Record<DerivedLead["stage"], string> = {
    new: "#7a766f", qualified: "#3D6B9D", proposal: "#A04612",
    won: "#3F7A55", lost: "#C95C1A",
  };

  STAGES.forEach((stage, i) => {
    const list = byStage.get(stage) ?? [];
    const bandCY = bandCenters[i] ?? 0;
    const stageId = `stage-${stage}`;
    nodes.push({
      id: stageId, type: "stage",
      position: { x: COL_STAGE_X, y: bandCY - STAGE_H / 2 },
      data: { stage, label: STAGE_LABEL[stage], count: list.length } as StageNodeData,
      draggable: false, selectable: false,
    });
    edges.push({
      id: `e-center-${stageId}`, source: "center", target: stageId,
      type: "smoothstep",
      style: { stroke: "#94908a", strokeWidth: 1.4 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#94908a", width: 12, height: 12 },
    });

    if (list.length === 0) return;

    // Stack leads symmetrically around the band center.
    const totalLeafH = list.length * LEAF_SLOT_H;
    const firstY = bandCY - totalLeafH / 2 + (LEAF_SLOT_H - LEAF_H) / 2;
    const color = stagePalette[stage];

    list.forEach((lead, j) => {
      const leafId = `lead-${lead.id}`;
      nodes.push({
        id: leafId, type: "lead",
        position: { x: COL_LEAF_X, y: firstY + j * LEAF_SLOT_H },
        data: { lead } as LeadNodeData,
        draggable: false, selectable: false,
      });
      edges.push({
        id: `e-${stageId}-${leafId}`, source: stageId, target: leafId,
        type: "smoothstep",
        style: { stroke: color, strokeWidth: 1.1 },
        markerEnd: { type: MarkerType.ArrowClosed, color, width: 11, height: 11 },
      });
    });
  });

  const width = COL_LEAF_X + LEAF_W + 60;
  return { nodes, edges, width, height: Math.max(560, totalHeight + 40) };
}

export function MindmapView(): JSX.Element {
  const { leads, source } = useStudioLeadsLive();
  const { nodes, edges, height: layoutH } = useMemo(() => layoutMindmap(leads), [leads]);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [h, setH] = useState(700);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Inline height grows with content (one card-row per lead in a
    // stage) so cards never overlap. Capped so the page doesn't get
    // ridiculous; expand modal handles the full pipeline anyway.
    setH(Math.min(820, Math.max(560, layoutH + 40)));
  }, [layoutH]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setExpanded(false); }
    if (expanded) {
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
      return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    }
    return () => { /* no-op */ };
  }, [expanded]);

  const flow = (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={mindmapNodeTypes}
      fitView
      fitViewOptions={{ padding: 0.14 }}
      minZoom={0.45}
      maxZoom={1.8}
      panOnDrag
      zoomOnScroll={false}
      zoomOnPinch
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      proOptions={{ hideAttribution: true }}
    >
      <Background gap={22} size={1} color="#ece7dc" />
      {expanded ? <Controls showInteractive={false} className="!shadow-sm" /> : null}
    </ReactFlow>
  );

  return (
    <div>
      <div ref={wrapRef} className="relative rounded-2xl border border-border/60 overflow-hidden bg-[radial-gradient(circle_at_50%_50%,rgba(201,92,26,0.04),transparent_70%)]" style={{ height: h }}>
        <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5">
          <span className={cls(
            "pill text-[9.5px] border",
            source === "agent"
              ? "bg-fern/10 text-fern border-fern/30"
              : "bg-cream-dark text-ink-500 border-border",
          )}>
            {source === "agent" ? "live · agent" : "from your projects"}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label="Expand"
          className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-cream/95 border border-border text-[11px] font-medium text-ink-700 hover:text-ink-950 backdrop-blur-sm shadow-sm transition"
        >
          <Maximize2 className="w-3 h-3" /> Expand
        </button>
        {flow}
      </div>

      {expanded ? createPortal((
        <AnimatePresence>
          <motion.div
            key="bg"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[80] bg-ink-950/40 backdrop-blur-sm"
            onClick={() => setExpanded(false)}
          />
          <motion.div
            key="card"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-4 sm:inset-8 z-[81] panel overflow-hidden flex flex-col"
          >
            <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border/60">
              <div>
                <div className="eyebrow">Studio mindmap · expanded</div>
                <div className="display-3 text-ink-950 leading-tight">Lead pipeline · {leads.length} leads</div>
              </div>
              <button onClick={() => setExpanded(false)} aria-label="Close" className="w-8 h-8 rounded-lg bg-cream-dark border border-border grid place-items-center text-ink-700 hover:text-ink-950 transition">
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="flex-1 bg-[radial-gradient(circle_at_50%_50%,rgba(201,92,26,0.04),transparent_70%)]">
              {flow}
            </div>
          </motion.div>
        </AnimatePresence>
      ), document.body) : null}
    </div>
  );
}

// Re-export the lucide icons used in the panel header for callers.
export { Building2, Mail, ShoppingBag };
